export const ACTION_PERMISSION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
] as const;

/**
 * Object IDs supplied by the FastAPI permission catalog. These IDs are used
 * with create/update/delete grants; page visibility uses PAGE_PERMISSION_KEYS.
 */
export const ACTION_PERMISSION_KEYS = {
  contact: "contact",
  contactPortalAccess: "contactPortalAccess",
  contactAddress: "contactAddress",
  contactEmail: "contactEmail",
  contactFax: "contactFax",
  contactPhone: "contactPhone",
  contactWebsite: "contactWebsite",
  contactConnection: "contactConnection",
  contactTag: "contactTag",
  contactTagConnection: "contactTagConnection",
  matter: "matter",
  matterAssignee: "matterAssignee",
  matterRelatedMatterConnection: "matterRelatedMatterConnection",
  matterIncidentReport: "matterIncidentReport",
  matterInsurance: "matterInsurance",
  matterLostWages: "matterLostWages",
  matterStatuteOfLimitations: "matterStatuteOfLimitations",
  matterVehicle: "matterVehicle",
  matterPropertyDamage: "matterPropertyDamage",
  matterExpense: "matterExpense",
  objectUpdate: "objectUpdate",
  matterLien: "matterLien",
  matterMedicalTreatment: "matterMedicalTreatment",
  matterMedicalTreatmentAppointment: "matterMedicalTreatmentAppointment",
  matterMedicalTreatmentAppointmentConnection:
    "matterMedicalTreatmentAppointmentConnection",
  matterMedicalTreatmentBalanceConfirmation:
    "matterMedicalTreatmentBalanceConfirmation",
  matterMedicalTreatmentBill: "matterMedicalTreatmentBill",
  matterMedicalTreatmentPlan: "matterMedicalTreatmentPlan",
  matterMedicalTreatmentRecord: "matterMedicalTreatmentRecord",
  matterSettlementCounter: "matterSettlementCounter",
  matterSettlementDemand: "matterSettlementDemand",
  matterSettlementDemandPackageSegment: "matterSettlementDemandPackageSegment",
  matterSettlementOffer: "matterSettlementOffer",
  matterLitigationDate: "matterLitigationDate",
  matterTrustCheck: "matterTrustCheck",
  matterTrustCheckConnection: "matterTrustCheckConnection",
  matterEmail: "matterEmail",
  matterFile: "matterFile",
  matterShareFolder: "matterShareFolder",
  matterShareFolderFile: "matterShareFolderFile",
  matterSignatureRequest: "matterSignatureRequest",
  matterTemplate: "matterTemplate",
  matterIntakeTemplate: "matterIntakeTemplate",
  matterIntakeTemplateQuestion: "matterIntakeTemplateQuestion",
  matterIntakeTemplateResponse: "matterIntakeTemplateResponse",
  matterRole: "matterRole",
  matterTeam: "matterTeam",
  matterTeammate: "matterTeammate",
  matterTeamConnection: "matterTeamConnection",
  incidentType: "matterType",
  matterStatus: "matterStatus",
  matterStatusPhase: "matterStatusPhase",
  matterTag: "matterTag",
  matterTagConnection: "matterTagConnection",
  matterTaskPlan: "matterTaskPlan",
  matterTaskPlanTask: "matterTaskPlanTask",
  matterSurvey: "matterSurvey",
  matterLitigationDatePlan: "matterLitigationDatePlan",
  matterLitigationDatePlanDate: "matterLitigationDatePlanDate",
  matterLitigationDateType: "matterLitigationDateType",
  matterScheduledMessage: "matterScheduledMessage",
  defaultMatterExpense: "defaultMatterExpense",
  defaultMatterFileRepositoryStructure: "defaultMatterFileRepositoryStructure",
  defaultMatterFileRepositoryStructureFolder:
    "defaultMatterFileRepositoryStructureFolder",
  leadSource: "leadSource",
  leadSourceMapping: "leadSourceMapping",
  massTort: "massTort",
  massTortSurvey: "massTortSurvey",
  massTortCustomField: "massTortCustomField",
  massTortMatterCustomField: "massTortMatterCustomField",
  firm: "firm",
  firmAppSubscription: "firmAppSubscription",
  firmOffice: "firmOffice",
  firmPhoneNumber: "firmPhoneNumber",
  firmContactConnection: "firmContactConnection",
  firmVirtualBusinessCard: "firmVirtualBusinessCard",
  user: "user",
  permissionGroup: "permissionGroup",
  firmFile: "firmFile",
  firmSignatureRequest: "firmSignatureRequest",
  firmSignatureRequestField: "firmSignatureRequestField",
  firmReferralProfile: "firmReferralProfile",
  firmReferralConnectionRequest: "firmReferralConnectionRequest",
  firmReferralNetworkConnection: "firmReferralNetworkConnection",
} as const;

export const ACTION_PERMISSION_VALUES = {
  create: "create",
  delete: "delete",
  update: "update",
} as const;

export type ActionPermissionKey =
  (typeof ACTION_PERMISSION_KEYS)[keyof typeof ACTION_PERMISSION_KEYS];
export type ActionPermissionValue =
  (typeof ACTION_PERMISSION_VALUES)[keyof typeof ACTION_PERMISSION_VALUES];

/**
 * Page IDs are supplied by the FastAPI permission catalog and granted through
 * the current user's assigned permission groups. Keep route admission checks
 * on these page IDs; object IDs belong to mutation authorization.
 */
export const PAGE_PERMISSION_KEYS = {
  dashboard: "dashboard",
  appsAccess: "apps.access",
  intakeAccess: "intake.access",
  intakeOpportunities: "intake.opportunities",
  intakeLeads: "intake.leads",
  intakeSettings: "intake.settings",
  intakeSettingsLeadSources: "intake.settings.leadSources",
  intakeSettingsRoles: "intake.settings.roles",
  intakeSettingsTeams: "intake.settings.teams",
  intakeSettingsTypes: "intake.settings.types",
  intakeSettingsStatuses: "intake.settings.statuses",
  intakeSettingsTags: "intake.settings.tags",
  intakeSettingsTaskPlans: "intake.settings.taskPlans",
  mattersAccess: "matters.access",
  mattersMy: "matters.my",
  mattersAll: "matters.all",
  mattersDetail: "matters.detail",
  mattersDetailOverview: "matters.detail.overview",
  mattersDetailParties: "matters.detail.parties",
  mattersDetailIncidentDetails: "matters.detail.incidentDetails",
  mattersDetailCustomFields: "matters.detail.customFields",
  mattersDetailPropertyDamage: "matters.detail.propertyDamage",
  mattersDetailEmails: "matters.detail.emails",
  mattersDetailTextMessages: "matters.detail.textMessages",
  mattersDetailLiens: "matters.detail.liens",
  mattersDetailCosts: "matters.detail.costs",
  mattersDetailMedicalTreatments: "matters.detail.medicalTreatments",
  mattersDetailSettlements: "matters.detail.settlements",
  mattersDetailLitigation: "matters.detail.litigation",
  mattersDetailTasks: "matters.detail.tasks",
  mattersDetailNotes: "matters.detail.notes",
  mattersDetailUpdates: "matters.detail.updates",
  mattersDetailFiles: "matters.detail.files",
  mattersDetailSignatureRequests: "matters.detail.signatureRequests",
  mattersDetailSurveys: "matters.detail.surveys",
  mattersClosed: "matters.closed",
  mattersDetailPhases: "matters.detail.phases",
  mattersReports: "matters.reports",
  mattersReportsDemands: "matters.reports.demands",
  mattersReportsSettlements: "matters.reports.settlements",
  mattersReportsRecordRequests: "matters.reports.recordRequests",
  mattersReportsApproachingStatutes: "matters.reports.approachingStatutes",
  mattersReportsActivity: "matters.reports.activity",
  mattersReportsPerStatus: "matters.reports.perStatus",
  mattersReportsRoleAssignments: "matters.reports.roleAssignments",
  mattersSettings: "matters.settings",
  mattersSettingsScheduledMessages: "matters.settings.scheduledMessages",
  mattersSettingsIntakeTemplates: "matters.settings.intakeTemplates",
  mattersSettingsRoles: "matters.settings.roles",
  mattersSettingsTeams: "matters.settings.teams",
  mattersSettingsTypes: "matters.settings.types",
  mattersSettingsStatuses: "matters.settings.statuses",
  mattersSettingsTags: "matters.settings.tags",
  mattersSettingsCosts: "matters.settings.costs",
  mattersSettingsTaskPlans: "matters.settings.taskPlans",
  mattersSettingsPhaseProjects: "matters.settings.phaseProjects",
  mattersSettingsLitigationPlans: "matters.settings.litigationPlans",
  mattersSettingsDefaultFiles: "matters.settings.defaultFiles",
  referralsAccess: "referrals.access",
  referralsAll: "referrals.all",
  referralsSettings: "referrals.settings",
  referralsSettingsRoles: "referrals.settings.roles",
  referralsSettingsTeams: "referrals.settings.teams",
  referralsSettingsTypes: "referrals.settings.types",
  referralsSettingsStatuses: "referrals.settings.statuses",
  referralsSettingsTags: "referrals.settings.tags",
  referralsSettingsTaskPlans: "referrals.settings.taskPlans",
  massTortsAccess: "massTorts.access",
  massTortsAll: "massTorts.all",
  massTortsDetail: "massTorts.detail",
  massTortsDetailOverview: "massTorts.detail.overview",
  massTortsDetailSurveys: "massTorts.detail.surveys",
  massTortsDetailTasks: "massTorts.detail.tasks",
  massTortsDetailCustomFields: "massTorts.detail.customFields",
  massTortsDetailNotes: "massTorts.detail.notes",
  massTortsDetailFileGeneration: "massTorts.detail.fileGeneration",
  massTortsDetailFiles: "massTorts.detail.files",
  filesAccess: "files.access",
  signatureRequestsAccess: "signatureRequests.access",
  contactsAccess: "contacts.access",
  contactsAll: "contacts.all",
  contactsDetail: "contacts.detail",
  contactsSettings: "contacts.settings",
  contactsSettingsTags: "contacts.settings.tags",
  firmAccess: "firm.access",
  firmMyFirm: "firm.myFirm",
  firmFiles: "firm.files",
  firmTemplates: "firm.templates",
  firmReports: "firm.reports",
  firmReportsAuditEvents: "firm.reports.auditEvents",
  firmReportsUserActivity: "firm.reports.userActivity",
  firmSettings: "firm.settings",
  firmSettingsUsers: "firm.settings.users",
  firmSettingsPhoneNumbers: "firm.settings.phoneNumbers",
} as const;

export const MATTER_PHASE_PERMISSION_KEYS = {
  phaseProject: "phaseProject",
  reusablePhase: "reusablePhase",
  matterPhaseApplication: "matterPhaseApplication",
  matterPhaseEvaluation: "matterPhaseEvaluation",
  matterPhaseOverride: "matterPhaseOverride",
} as const;
